import createEmotionCache from "@emotion/cache";
import { CacheProvider, Global } from "@emotion/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { type ReactNode } from "react";

import { DesignerThemeProvider } from "@skilitics/designer-theme";
import { designerIconLibrary, ErrorBoundary } from "@skilitics/designer-ui-components";
import { useActiveDesignerThemeName } from "@skilitics/nautilus-core-designer";

import { queryClient } from "../api/queryClient.ts";
import { AppRouter } from "../landing-page";
import { AppCore } from "./AppCore";
import { DesignerProfiling } from "./DesignerProfiling";
import { globalStyles } from "./GlobalStyles";
import { KeyboardShortcutsDialog } from "./KeyboardShortcutsDialog";
import { useAppKeyboardShortcuts } from "./useKeyboardShortcuts";

// Disable the builtin auto-prefix plugin which isn't needed in any current browser.
const emotionCache = createEmotionCache({
  key: "skd",
  stylisPlugins: [],
});

export const DesignerThemeWrapper = ({ children }: { children: ReactNode }) => {
  const themeName = useActiveDesignerThemeName();

  return <DesignerThemeProvider themeName={themeName}>{children}</DesignerThemeProvider>;
};

designerIconLibrary();

export const App = () => {
  useAppKeyboardShortcuts();

  return (
    <ErrorBoundary>
      <CacheProvider value={emotionCache}>
        <QueryClientProvider client={queryClient}>
          <DesignerThemeWrapper>
            <Global styles={globalStyles} />
            {import.meta.env.DEV && <DesignerProfiling />}
            <AppCore>
              <AppRouter />
            </AppCore>
            <KeyboardShortcutsDialog />
          </DesignerThemeWrapper>
          <ReactQueryDevtools buttonPosition="bottom-right" />
        </QueryClientProvider>
      </CacheProvider>
    </ErrorBoundary>
  );
};
